// frontend/src/components/Conversations.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, updateDoc, doc, getDoc, deleteDoc } from 'firebase/firestore';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const ConversationItem = ({ convo, onReanalyze, role }) => {
    const [expanded, setExpanded] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const { analysis, messages, timestamp, source, status, modelId, platform } = convo;

    // Helper: Copy Summary
    const handleCopy = () => {
        const text = `
Nickname: ${analysis?.nickname || 'N/A'}
Name: ${analysis?.name || 'N/A'}
Age: ${analysis?.age || 'N/A'}
Location: ${analysis?.location || 'N/A'}
Interests: ${analysis?.interests || 'N/A'}
Summary: ${analysis?.summary || 'N/A'}
        `.trim();
        navigator.clipboard.writeText(text);
        alert("Summary copied to clipboard!");
    };

    // Helper: Trigger Re-analyze
    const handleReanalyzeClick = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Re-run AI analysis on this chat? This overwrites existing data.")) return;
        
        setAnalyzing(true);
        await onReanalyze(convo.id, messages, convo.modelId);
        setAnalyzing(false);
    };

    // Format Date
    const dateStr = timestamp?.seconds ? new Date(timestamp.seconds * 1000).toLocaleString() : 'Just now';

    return (
        <div style={itemStyle} className="list-item">
            {/* COLLAPSED HEADER */}
            <div 
                onClick={() => setExpanded(!expanded)} 
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '5px' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#007acc' }}>
                            {analysis?.nickname || 'Unanalyzed Chat'}
                        </span>
                        <span style={tagStyle}>{modelId}</span>
                        <span style={{ ...tagStyle, background: '#6a0dad' }}>{platform || 'Unknown Platform'}</span>
                        
                        {/* ADMIN ONLY TAGS */}
                        {role === 'admin' && (
                            <>
                                <span style={{ ...tagStyle, background: '#333' }}>{source || 'Unknown Source'}</span>
                                <span style={{ 
                                    ...tagStyle,
                                    background: status === 'CONVERTED' ? '#0f390f' 
                                              : (status === 'SKIPPED' || status === 'DQ') ? '#390f0f' 
                                              : (status === 'USER_SKIPPED' || status === 'USER_LEFT') ? '#424242' 
                                              : status === 'USER_AFK' ? '#5c4e00' 
                                              : '#333',
                                    color: status === 'CONVERTED' ? '#00ff00' 
                                         : (status === 'SKIPPED' || status === 'DQ') ? '#ff0000' 
                                         : (status === 'USER_SKIPPED' || status === 'USER_LEFT') ? '#e0e0e0' 
                                         : status === 'USER_AFK' ? '#ffd700' 
                                         : '#aaa'
                                }}>
                                    {status}
                                </span>
                            </>
                        )}
                    </div>
                    <div style={{ color: '#888', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '600px' }}>
                        {analysis?.summary || 'No summary available...'}
                    </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#666' }}>
                    <div>{dateStr}</div>
                    <div style={{ marginTop: '5px' }}>{expanded ? '▲ Close' : '▼ Expand'}</div>
                </div>
            </div>

            {/* EXPANDED CONTENT */}
            {expanded && (
                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #333', display: 'flex', gap: '20px', height: '400px', overflow: 'hidden' }}>
                    
                    {/* LEFT: ANALYSIS & METADATA */}
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#e0e0e0' }}>📋 Lead Intelligence</h4>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <button onClick={handleReanalyzeClick} disabled={analyzing} style={smBtnStyle}>
                                    {analyzing ? 'Thinking...' : '⚡ Re-Analyze'}
                                </button>
                                <button onClick={handleCopy} style={smBtnStyle}>📋 Copy</button>
                            </div>
                        </div>

                        <div style={dataBoxStyle}>
                            <p><strong>Name:</strong> {analysis?.name}</p>
                            <p><strong>Age:</strong> {analysis?.age}</p>
                            <p><strong>Loc:</strong> {analysis?.location}</p>
                            <p><strong>Interests:</strong> {analysis?.interests}</p>
                            <hr style={{ borderColor: '#444' }}/>
                            <p style={{ lineHeight: '1.5', color: '#ccc' }}>{analysis?.summary}</p>
                        </div>
                    </div>

                    {/* RIGHT: CHAT HISTORY */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #333', paddingLeft: '10px', minHeight: 0 }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#e0e0e0' }}>💬 Transcript</h4>
                        <div style={{ flex: '1 1 0', overflowY: 'auto', background: '#111', padding: '10px', borderRadius: '4px', border: '1px solid #333' }}>
                            {messages?.map((m, i) => (
                                <div key={i} style={{ marginBottom: '8px', fontSize: '0.85rem' }}>
                                    <span style={{ 
                                        color: m.role === 'user' ? '#4ec9b0' : (m.role === 'assistant' || m.role === 'model') ? '#ce9178' : '#888',
                                        fontWeight: 'bold' 
                                    }}>
                                        {m.role === 'user' ? 'FAN' : (m.role === 'assistant' || m.role === 'model') ? (modelId || 'MODEL') : 'SYS'}:
                                    </span>
                                    <span style={{ marginLeft: '5px', color: '#ccc' }}>{m.content}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default function Conversations({ role }) {
    const { currentUser } = useAuth();
    const [chats, setChats] = useState([]);
    
    // Filters
    const [filterModel, setFilterModel] = useState('');
    const [filterPlatform, setFilterPlatform] = useState('');
    
    // Options Data
    const [allModels, setAllModels] = useState([]);
    const [allPlatforms, setAllPlatforms] = useState([]);

    const [loading, setLoading] = useState(false);

    // --- DELETE LOGIC START ---
    const [isEditing, setIsEditing] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) setSelectedIds(prev => prev.filter(i => i !== id));
        else setSelectedIds(prev => [...prev, id]);
    };

    const handleDeleteSelected = async () => {
        if (!window.confirm(`Delete ${selectedIds.length} chats? Cannot be undone.`)) return;
        try {
            await Promise.all(selectedIds.map(id => deleteDoc(doc(db, "conversations", id))));
            setChats(prev => prev.filter(c => !selectedIds.includes(c.id)));
            setSelectedIds([]);
            setIsEditing(false);
            alert("Deleted.");
        } catch (e) { alert(e.message); }
    };
    // --- DELETE LOGIC END ---

    // 1. Fetch Options (Models & Platforms) on Mount
    useEffect(() => {
        const loadOptions = async () => {
            // Models
            const mSnap = await getDocs(collection(db, "models"));
            const mList = []; 
            mSnap.forEach(d => mList.push(d.data().modelId));
            setAllModels(mList);

            // Platforms
            try {
                const pDoc = await getDoc(doc(db, "settings", "platforms"));
                if (pDoc.exists()) {
                    setAllPlatforms(pDoc.data().list || ['Emerald Chat']);
                } else {
                    setAllPlatforms(['Emerald Chat']);
                }
            } catch (e) {
                console.error("Error loading platforms:", e);
                setAllPlatforms(['Emerald Chat']);
            }
        };
        loadOptions();
    }, []);

    // Fetch Logic
    const fetchChats = async () => {
        setLoading(true);
        try {
            let q = query(collection(db, "conversations"), orderBy("timestamp", "desc"));

            // Role-based filtering
            if (role === 'chatter') {
                if (!currentUser?.assignedModels || currentUser.assignedModels.length === 0) {
                    setChats([]);
                    setLoading(false);
                    return;
                }
                // 🔒 CHATTERS ONLY SEE CONVERTED LEADS
                q = query(
                    q, 
                    where("modelId", "in", currentUser.assignedModels),
                    where("status", "==", "CONVERTED")
                );
            }

            const snap = await getDocs(q);
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));

            // Client-side filtering (Applies to both Admin and Chatter)
            let filtered = list;
            if (filterModel) {
                filtered = filtered.filter(c => c.modelId === filterModel);
            }
            if (filterPlatform) {
                filtered = filtered.filter(c => c.platform === filterPlatform);
            }
            setChats(filtered);

        } catch (error) {
            console.error("Fetch error:", error);
            alert("Error loading chats. Check console.");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchChats();
    }, [role, currentUser, filterModel, filterPlatform]);

    // Helper: Clear Filters
    const clearFilters = () => {
        setFilterModel('');
        setFilterPlatform('');
    };

    // Re-analyze Action
    const reAnalyzeChat = async (docId, history, modelId) => {
        try {
            // Call Backend
            // NOTE: We use the existing API Key from localStorage for convenience, 
            // or we could store a system key. For now, we grab it from localStorage to keep it simple.
            const apiKey = localStorage.getItem('shield_api_key'); 
            const model = localStorage.getItem('shield_model') || 'nousresearch/hermes-3-llama-3.1-405b:free';

            if (!apiKey) {
                alert("Missing API Key. Please go to Chat Sandbox and enter one.");
                return;
            }

            const res = await axios.post('http://127.0.0.1:8000/api/chat/analyze', {
                apiKey: apiKey,
                model: model, 
                messages: history,
                source: 'Re-Analyze Tool'
            });

            const newAnalysis = res.data;

            // Update DB
            const docRef = doc(db, "conversations", docId);
            await updateDoc(docRef, {
                analysis: newAnalysis
            });

            // Refresh Local State
            setChats(prev => prev.map(c => c.id === docId ? { ...c, analysis: newAnalysis } : c));

        } catch (error) {
            alert(`Analysis failed: ${error.message}`);
        }
    };

    return (
        <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>🗄️ Conversation History</h3>
                
                {/* Filters (Admin & Chatter) */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    {/* Show dropdown if Admin OR if Chatter has assignments */}
                    {(role === 'admin' || (role === 'chatter' && currentUser?.assignedModels?.length > 0)) && (
                        <select 
                            value={filterModel} 
                            onChange={(e) => setFilterModel(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">-- All My Models --</option>
                            {/* Logic: If Admin, show ALL. If Chatter, show only ASSIGNED. */}
                            {(role === 'admin' ? allModels : currentUser?.assignedModels || []).map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    )}

                    <select 
                        value={filterPlatform} 
                        onChange={(e) => setFilterPlatform(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">-- All Platforms --</option>
                        {allPlatforms.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    
                    <button onClick={clearFilters} style={{...btnStyle, background: '#444'}}>Clear Filters</button>

                    {/* ADMIN DELETE BUTTONS */}
                    {role === 'admin' && (
                        <>
                            <div style={{ width: '1px', background: '#555', height: '30px', margin: '0 10px' }}></div>
                            <button 
                                onClick={() => { setIsEditing(!isEditing); setSelectedIds([]); }}
                                style={{ ...btnStyle, background: isEditing ? '#666' : '#007bff' }}
                            >
                                {isEditing ? "Cancel" : "Edit List"}
                            </button>
                            {isEditing && (
                                <button
                                    onClick={handleDeleteSelected}
                                    disabled={selectedIds.length === 0}
                                    style={{ ...btnStyle, background: '#dc3545', opacity: selectedIds.length ? 1 : 0.5 }}
                                >
                                    Delete ({selectedIds.length})
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? <p>Loading archives...</p> : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {chats.length === 0 && <p style={{ color: '#888' }}>No conversations found.</p>}
                        {chats.map(c => (
                            <div key={c.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                {isEditing && (
                                    <div style={{ paddingTop: '20px' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(c.id)}
                                            onChange={() => toggleSelect(c.id)}
                                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                        />
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    {/* Added role={role} so the item knows if it's a chatter or admin */}
                                    <ConversationItem convo={c} onReanalyze={reAnalyzeChat} role={role} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Styles
const inputStyle = { padding: '8px', background: '#3c3c3c', border: '1px solid #555', color: 'white', borderRadius: '4px' };
const itemStyle = { background: '#1e1e1e', padding: '15px', border: '1px solid #444', borderRadius: '4px' };
const tagStyle = { fontSize: '0.75rem', padding: '2px 6px', borderRadius: '3px', background: '#007acc', color: 'white' };
// Fix: Added maxHeight and overflowY to create a scrollbar for long summaries
const dataBoxStyle = { 
    background: '#111', 
    padding: '15px', 
    borderRadius: '4px', 
    border: '1px dashed #444', 
    fontSize: '0.9rem', 
    color: '#ccc',
    maxHeight: '330px', // Fits nicely within the 400px parent container
    overflowY: 'auto' 
};
const btnStyle = { padding: '8px 15px', background: '#2da44e', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' };
const smBtnStyle = { padding: '4px 10px', background: '#444', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px', fontSize: '0.8rem' };